<script lang="ts" setup>import { ref as _ref } from 'vue';

import HowGiftlyWorksLanding from '@consumer/modules/howGiftlyWorks/HowGiftlyWorksLanding.vue'
import type { RecipientGift } from '@consumer/types/serializers'
import { useElementSize, useStepper } from '@vueuse/core'
import { useSharedData } from '@consumer/composables/sharedData'
import { useDefaultTheme } from '@consumer/composables/themes'
import V2ModalFullscreen from '@consumer/components/V2/V2ModalFullscreen.vue'
import HeroContainer from '@consumer/modules/recipientGiftLandingV2/components/HeroContainer.vue'
import OpenGift from '@consumer/modules/recipientGiftLandingV2/components/OpenGift.vue'
import HeroHeader from '@consumer/modules/recipientGiftLandingV2/components/HeroHeader.vue'
import { useMotion } from '@vueuse/motion'
import { isDesktop } from '@consumer/utils/breakpoints'
import { setupRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import { sleep } from '@shared/timers'

export type DemoStep = 'how-giftly-works-landing' | 'preview-landing'

withDefaults(defineProps<{
  initialStep?: DemoStep
  gift?: RecipientGift
}>(), { gift: () => ({
    id: '',
    giftlyVersion: 3,
    amount: 75,
    bookmarkedPlaces: [],
    places: [{ id: 'any restaurant', name: 'Any Restaurant' }],
    itemDescription: 'Any Restaurant',
    giftlyMetadata: {
      designName: 'gradient-5',
      themeColor: '#208DCA',
      maskColor: '#208DCA',
      logoColor: '#ffffff',
      greetingCardColor: '#FF91AF',
      maskName: 'giftly-gradient-mask-3',
      tagline: 'Any Restaurant',
    },
    greeting: {
      from: 'Me',
      to: 'You',
      message: 'Thank you for everything! I hope you have a great day. Looking forward to seeing you soon.',
    },
    recipientLandingUrl: '',
    recipientSupportUrl: '',
    openedAt: new Date(),
    redemptionConfirmedAt: new Date(),
    redemptionCreditSentAt: new Date(),
    redemptionBankName: 'Bank of America',
    redemptionBankAccountNumberLast4: '1234',
    redemptionPaypalEmail: 'johndoe@example.com',
    redemptionShippingAddress: {
      name: 'John Doe',
      street: '123 Main St',
      city: 'San Francisco',
      state: 'CA',
      zip: '94105',
    },
  }), })

const { processingBank } = useSharedData()

const open = ref(true)

const emit = defineEmits<{ close: [] }>()
const onClose = () => {
  emit('close')
  open.value = false
}

useDefaultTheme()

const {
  primaryBgColor,
  secondaryBgColor,
} = setupRecipientLanding(() => __props.gift as RecipientGift)

const hasNote = computed(
  () => !!(__props.gift as RecipientGift).greeting?.message,
)

const storySlides = computed(
  () => (__props.gift as RecipientGift).greeting?.greetingCardCustomization?.storySlides ?? [],
)

const hasVoiceMemo = computed(() => {
  const vm = (__props.gift as RecipientGift).greeting?.greetingCardCustomization?.voiceMemo
  return Boolean(vm)
})

const photoStepNames = computed(() =>
  storySlides.value.map((_, i) => `preview-photo-${i}`),
)

const allSteps = computed(() => [
  'how-giftly-works-landing',
  'preview-landing',
  'preview-open',
  ...(hasVoiceMemo.value ? ['preview-voice-memo'] : []),
  ...photoStepNames.value,
  'preview-giftly',
  'preview-giftly-redemption',
  'preview-thank-you',
])

const stepper = useStepper(allSteps)

const progressSteps = computed(() =>
  allSteps.value.filter(s => s !== 'how-giftly-works-landing'),
)

const next = computed(() => stepper.next.value ? stepper.get(stepper.next.value) : null)

const openGift = _ref<InstanceType<typeof OpenGift>>()
let canTransition = _ref(false)
const readyToGoToNext = computed(() => canTransition.value && openGift.value?.canTransition)
const goToNext = () => {
  stepper.goToNext()
}

onMounted(() => {
  stepper.goTo(__props.initialStep ?? 'how-giftly-works-landing')
})

let demoDescription = _ref('Preview the gift experience')

watch(
  () => stepper.current.value,
  () => {
    canTransition.value = false
    if (stepper.isCurrent('preview-landing')) {
      demoContainerMotion.apply('expand')
      openGift.value?.goTo('ENVELOPE')
      demoBlurbMotion?.apply('initial')
      demoDescription.value = 'Preview the gift experience'
      canTransition.value = true
    }

    if (stepper.isCurrent('preview-open')) {
      demoBlurbMotion?.apply('hide')?.then(() => {
        demoDescription.value = 'Your recipient opens your gift'
        sleep(1000).then(() => {
          demoContainerMotion.apply('expand')
          demoDescription.value = 'The greeting card you select is displayed'
          openGift.value?.goTo('GREETING_CARD_REVEAL').then(() => {
            sleep(1000).then(() => {
              if (hasNote.value) {
                demoContainerMotion.apply('expand')
                demoBlurbMotion?.apply('hide')
                openGift.value?.goTo('GREETING_CARD_OPEN')
                demoDescription.value = 'Your recipient will appreciate your personal message'
                canTransition.value = true
              }
              else {
                stepper.goToNext()
              }
            })
          })
        })
      })
    }

    const currentStep = stepper.current.value
    if (currentStep.startsWith('preview-photo-')) {
      openGift.value?.goTo('ENVELOPE')
      const slideIndex = Number(currentStep.split('-')[2])
      demoContainerMotion.apply('expand')
      demoBlurbMotion?.apply('hide')
      openGift.value?.goTo(`PHOTO_SLIDE_${slideIndex}` as any)
      if (hasNote.value)
        demoDescription.value = '...and the photos you uploaded'
      else
        demoDescription.value = 'The recipient will see the photos you uploaded'
      canTransition.value = true
    }

    if (stepper.isCurrent('preview-voice-memo')) {
      openGift.value?.goTo('ENVELOPE')
      demoContainerMotion.apply('expand')
      demoBlurbMotion?.apply('hide')
      openGift.value?.goTo('VOICE_MEMO')
      demoDescription.value = 'They\'ll hear the voice memo you recorded'
      canTransition.value = true
    }

    if (stepper.isCurrent('preview-giftly')) {
      demoContainerMotion.apply('expand')
      demoBlurbMotion?.apply('hide')
      openGift.value?.goTo('GIFTLY_REVEAL')
      demoDescription.value = 'Then they\'ll see the gift and explore its associated places'
      canTransition.value = true
    }

    if (stepper.isCurrent('preview-giftly-redemption')) {
      demoContainerMotion.apply('expand')
      demoBlurbMotion?.apply('hide')
      demoDescription.value = 'They\'ll decide how to receive the gifted funds'
      canTransition.value = true
      openGift.value?.goTo('GIFTLY_REDEMPTION').then(() => {
        stepper?.goTo('preview-thank-you')
      })
    }

    if (stepper.isCurrent('preview-thank-you')) {
      demoContainerMotion.apply('expand')
      demoBlurbMotion?.apply('hide')
      openGift.value?.goTo('THANK_YOU')
      demoDescription.value = 'Then they go and get exactly what they want'
      canTransition.value = true
    }
  },
  { immediate: true },
)

const heroContainer = ref<InstanceType<typeof HeroContainer>>()
const demoContainerMotion = useMotion(heroContainer, {
  initial: {
    opacity: 1,
    width: isDesktop.value ? '560px' : '390px',
    height: isDesktop.value ? '550px' : '450px',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
  },
  expand: {
    opacity: 1,
    width: '',
    height: isDesktop.value ? '550px' : 'calc(100dvh - 192px)',
    'transform-origin': 'top',
    borderTopRadius: '0px',
    transition: { ease: 'easeInOut', duration: 200 },
  },
})

const demoBlurb = ref<HTMLElement>()
const demoBlurbMotion = useMotion(demoBlurb, {
  initial: {
    opacity: 1,
    y: 0,
  },
  hide: {
    opacity: 0,
    y: 20,
    transition: { ease: 'easeInOut', duration: 200 },
  },
})

const { height: heroContainerHeight } = useElementSize(heroContainer)
const scale = computed(() => isDesktop.value ? 1 : Math.min(heroContainerHeight.value / 652, 1.0))

</script>

<template>
  <V2ModalFullscreen
    name="demo-modal"
    class="demo-modal"
    :open="open"
    :pageTitle="{ text: stepper.isCurrent('how-giftly-works-landing') ? 'How Giftly Works' : 'Giftly Preview' }"
    @close="onClose"
  >
    <template #close-button>
      <slot name="close-button"/>
    </template>

    <div
      class="flex flex-col items-center text-center lg:py-6
      max-w-md lg:max-w-screen-lg mx-auto"
    >
      <TransitionGroup name="demo-steps" tag="div" class="w-full flex justify-center">
        <HowGiftlyWorksLanding
          v-if="stepper.isCurrent('how-giftly-works-landing')"
          key="landing"
        />

        <div v-else class="w-full flex flex-col justify-center items-center transition-all ease-in-out">
          <HeroContainer ref="heroContainer">
            <HeroHeader class="transition-all duration-500 ease-in-out !h-[64px] !lg:h-auto">
              {{ demoDescription }}
            </HeroHeader>
            <div
              class="h-full w-full flex flex-col items-center justify-center
              transition-all duration-750 ease-in-out"
              :class="{ 'pointer-events-none cursor-wait': !readyToGoToNext }"
              @click="goToNext"
            >
              <OpenGift
                ref="openGift"
                :gift="gift as any"
                :demo="true"
                class="mt-12"
                :style="{ scale: scale }"
              />
            </div>
            <div ref="demoBlurb" class="absolute w-full bottom-0 p-6">
              See how simple and memorable it is to send a gift that is truly personal.
            </div>
          </HeroContainer>

          <div class="relative w-full p-6">
            <div class="absolute inset-0 flex justify-center gap-4 py-3 bg-white">
              <template v-for="step in progressSteps" :key="step">
                <div
                  class="h-2 rounded-v2md transition-all duration-200 ease-in-out"
                  :class="openGift?.canTransition ? 'cursor-pointer':'pointer-events-none cursor-wait'"
                  :style="{
                    background: stepper.isCurrent(step as any) ? primaryBgColor : secondaryBgColor,
                    width: stepper.isCurrent(step as any) ? '80px' : '40px',
                  }"
                  @click="stepper.goTo(step as any)"
                />
              </template>
            </div>
          </div>
        </div>
      </TransitionGroup>

    </div>

    <div
      class="w-full p-6 bg-surface-grey text-grey-500
        text-xs px-6 text-balance text-center"
      style="margin-top: 200px;"
    >
      <template v-if="processingBank === 'sunrise'">
        The Giftly Prepaid Gift is issued by Sunrise Banks N.A., St. Paul, MN 55103, Member FDIC.
      </template>
      <template v-else>
        Gifts awaiting claim by Recipients are held by First International Bank & Trust until redeemed, cancelled,
        or escheated.
      </template>
      * Standard data rates, fees, and charges may apply to send by text.
    </div>

    <div style="height: 96px;"/>

    <template #actions>
      <div class="w-full flex flex-col items-center lg:px-4">
        <div v-if="stepper.isCurrent('how-giftly-works-landing')" class="w-full">
          <div class="lg:hidden w-full flex justify-between gap-4">
            <GButton
              name="preview-experience-button"
              variant="secondary"
              label="Preview"
              class="lg:hidden basis-1/2"
              @click="stepper.goToNext()"
            />
            <GButton
              name="close-button"
              variant="primary"
              label="Ok, got it!"
              @click="onClose()"
            />
          </div>
          <div class="hidden lg:flex max-w-screen-lg mx-auto justify-between">
            <GButton
              name="preview-experience-button"
              variant="secondary"
              label="Preview Experience"
              @click="stepper.goToNext()"
            />
            <GButton
              name="close-button"
              variant="primary"
              label="Ok, got it!"
              @click="onClose()"
            />
          </div>

        </div>
        <div v-else class="w-full max-w-screen-lg flex justify-center items-center my-1 gap-4">
          <div class="flex-1 lg:flex-none lg:w-360px flex" style="gap: 6px;">
            <GPreviousButton
              v-if="!stepper.isCurrent(initialStep as any)"
              @click="stepper.goToPrevious()"
            />
            <GButton
              v-if="next"
              :disabled="!readyToGoToNext"
              name="next-button"
              variant="primary"
              class="w-full"
              label="Next"
              @click="goToNext"
            />
            <GButton
              v-else
              name="close-button"
              variant="primary"
              class="w-full"
              label="Okay, great! 🎉"
              @click="onClose"
            />
          </div>
        </div>
      </div>
    </template>
  </V2ModalFullscreen>
</template>

<style lang="scss">
.demo-steps-enter-active,
.demo-steps-leave-active {
  transition: all 0.2s ease-in-out;
}

.demo-steps-enter-from,
.demo-steps-leave-to {
  opacity: 0;
}

.demo-steps-leave-active {
  position: absolute;
}
</style>
